import React from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useContent } from "@/contexts/ContentContext";
import { iconFor } from "@/lib/icons";
import { resolveCtaHref, isExternalCta } from "@/lib/cta";
import { trackCta } from "@/lib/analytics";

const eventForKey = (key) => {
    if (key === "awsai") return "awsai_click";
    if (key === "smart-work") return "smart_work_consultation_click";
    if (key === "research-workshop") return "research_workshop_enquiry_click";
    return "whatsapp_click";
};

export const Services = () => {
    const { content } = useContent();
    const s = content.services;
    const items = (s.items || []).filter((it) => it.visible !== false);

    return (
        <section id="services" data-testid="services-section" className="bg-slate-50 py-24 lg:py-28">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="max-w-3xl">
                    <p className="label-eyebrow">{s.eyebrow}</p>
                    <h2 data-testid="services-title" className="mt-4 font-display font-semibold text-slate-900 text-3xl sm:text-4xl lg:text-[42px] tracking-tight leading-[1.1]">
                        {s.title}
                    </h2>
                    <p className="mt-5 text-base lg:text-lg text-slate-600 font-body leading-relaxed">{s.subtitle}</p>
                </div>

                <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((item, idx) => {
                        const Icon = iconFor(item.icon);
                        const href = resolveCtaHref(item, content.contact);
                        const external = isExternalCta(item);
                        const testId = `service-card-${item.key}`;
                        return (
                            <article key={item.key} data-testid={testId} className="card-lift bg-white border border-slate-200 p-8 flex flex-col">
                                <div className="flex items-start justify-between">
                                    <span className="inline-flex items-center justify-center w-12 h-12 bg-slate-900 text-teal-300">
                                        <Icon strokeWidth={1.5} className="w-6 h-6" />
                                    </span>
                                    <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-medium">0{idx + 1}</span>
                                </div>
                                <h3 className="mt-6 font-display text-xl font-semibold text-slate-900 leading-snug">{item.title}</h3>
                                <p className="mt-3 text-[14.5px] text-slate-600 font-body leading-relaxed">{item.short}</p>
                                {item.long && (<p className="mt-3 text-[13.5px] text-slate-500 font-body leading-relaxed">{item.long}</p>)}
                                {item.suitable && (
                                    <p className="mt-4 text-[12.5px] text-slate-500 leading-relaxed">
                                        <span className="font-semibold text-slate-700">Suitable for: </span>{item.suitable}
                                    </p>
                                )}
                                <div className="mt-6 pt-5 border-t border-slate-100">
                                    <a
                                        href={href}
                                        target={external ? "_blank" : undefined}
                                        rel={external ? "noopener noreferrer" : undefined}
                                        data-testid={`${testId}-cta`}
                                        onClick={() => trackCta(eventForKey(item.key), { source: "service_card", label: item.key })}
                                        className="btn-link-teal"
                                    >
                                        {item.cta_label}
                                        {item.key === "awsai" ? <ArrowUpRight className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                                    </a>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Services;
